/**
 * Simplified API Client
 * Single interface for all HTTP requests to DailyReportAPI
 */

import { getApiEndpoint } from './apiEndpoint';
import { getCurrentUser } from '@aws-amplify/auth';

// Public operations that should not require auth headers
const PUBLIC_OPERATIONS = new Set(['getInviteStatus', 'validateInvite']);

/**
 * Simple HTTP client for DailyReportAPI
 */
class ApiClient {
  constructor() {
    this.baseUrl = getApiEndpoint();
  }

  // Network connectivity test
  async testConnection() {
    const url = `${this.baseUrl}/api`;
    try {
      // First, try a simple GET request to test network
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return true;
    } catch (error) {
      console.error('Network connectivity test failed:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return false;
    }
  }

  async getAuthHeaders() {
    try {
      // Always use fetchAuthSession which handles token refresh properly
      const { fetchAuthSession } = await import('@aws-amplify/auth');
      const session = await fetchAuthSession({ forceRefresh: false });
      
      if (session.tokens && session.tokens.accessToken) {
        const token = session.tokens.accessToken.toString();
        return {
          'Authorization': `Bearer ${token}`
        };
      }
      
      throw new Error('No access token available in session');
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication required');
    }
  }

  async request(endpoint, data = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Retry logic for token refresh issues
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Determine if this request is public (no auth required)
        const op = data && data.operation;
        const isPublic = PUBLIC_OPERATIONS.has(op);

        // Get authentication headers only for protected operations
        let authHeaders = {};
        if (!isPublic) {
          authHeaders = await this.getAuthHeaders();
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify(data)
        });

        // If we get a 401/403 on first attempt for protected ops, try refreshing tokens
        if (!isPublic && (response.status === 401 || response.status === 403) && attempt === 0) {
          // Force refresh tokens and retry
          const { fetchAuthSession } = await import('@aws-amplify/auth');
          await fetchAuthSession({ forceRefresh: true });

          lastError = new Error(`Authentication failed (${response.status}), retrying with fresh token`);
          continue;
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`API error response: ${errorText}`);
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        return result;

      } catch (error) {
        console.error(`API request failed (attempt ${attempt + 1}): ${endpoint}`, error);
        lastError = error;

        // Don't retry network errors or non-auth errors on subsequent attempts
        if (attempt > 0 || (error.name === 'TypeError' && error.message.includes('Failed to fetch'))) {
          break;
        }

        // Wait briefly before retry
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }

    // Provide more specific error messages for the final error
    if (lastError.name === 'TypeError' && lastError.message.includes('Failed to fetch')) {
      // Run connectivity test to provide more detailed diagnostics
      await this.testConnection();
      throw new Error(`Network error: Could not connect to API server at ${url}. Please check your internet connection and ensure the API is deployed.`);
    }

    throw lastError;
  }

  // All operations go through single unified endpoint
  async clinics(data) {
    return this.request('/api', data);
  }

  async users(data) {
    return this.request('/api', data);
  }

  async invites(data) {
    return this.request('/api', data);
  }

  async multiClinic(data) {
    return this.request('/api', data);
  }

  async topics(data) {
    return this.request('/api', data);
  }

  async reports(data) {
    return this.request('/api', data);
  }

  async quotas(data) {
    return this.request('/api', data);
  }

  async patientRequests(data) {
    return this.request('/api', data);
  }
}

// Export singleton
export const apiClient = new ApiClient();
export default apiClient;

// Also export for debugging in browser console
if (typeof window !== 'undefined') {
  window.apiClient = apiClient;
}
